import { useRef, useState, useEffect } from "react";

import moment from "moment";

import { usePrettyLogEventPage } from "@components/v2/analytics";
import { QuickFilterSelector } from "@features/quick-filter-selector";
import { useFeatureFlags } from "@hooks/http";
import { todayLocalAsISODate } from "@north-beam/nb-common";
import { parseDateRangeArgument } from "@utils/index";

import { useProductAnalyticsContext } from "../hooks";
import { quickSelectOptions } from "../metricsData";

import ChartControlsModal from "./chart-controls-modal";

export const ChartControl = () => {
  const prettyLogEventPage = usePrettyLogEventPage();
  const {
    filters,
    setSorting,
    setSelectedProducts,
    setSelectedPlatforms,
    setSelectedCampaigns,
    setSelectedAds,
    setQuickSelect,
    selectedQuickSelect,
  } = useProductAnalyticsContext();
  const { hasFeatureFlag } = useFeatureFlags();

  const dateRange = parseDateRangeArgument(
    filters.dateRange,
    todayLocalAsISODate(),
  );

  const [chartControlsModalOpen, setChartControlsModalOpen] = useState(false);

  const [isSticky, setIsSticky] = useState(false);
  const headerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsSticky(!entry.isIntersecting);
      },
      { root: null, threshold: 0, rootMargin: "-150px 0px 0px 0px" },
    );
    if (headerRef.current) {
      observer.observe(headerRef.current);
    }
    return () => observer.disconnect();
  }, []);

  return (
    <>
      <div
        ref={headerRef}
        className={`flex px-6 items-center py-4.5 gap-2 sticky top-0 z-50 bg-v2-glass-inverse backdrop-blur-sm transition delay-150 duration-300 ${
          isSticky ? "drop-shadow-[0_0px_10px_rgba(0,0,0,0.1)]" : ""
        }`}
      >
        <button
          onClick={() => setChartControlsModalOpen(true)}
          className="rounded-lg border border-v2-secondary bg-v2-primary px-4 py-[7px] overflow-hidden text-v2-primary
         text-ellipsis font-normal text-sm max-w-[250px] shrink-0"
        >
          {moment(dateRange.startDate).format("MMM D, YYYY")}-
          {moment(dateRange.endDate).format("MMM D, YYYY")}
          <i className="fa-regular fa-calendar ml-2 text-v2-primary"></i>
        </button>
        {hasFeatureFlag("enableDashboardV2") && (
          <QuickFilterSelector
            options={quickSelectOptions({
              setSorting,
              setSelectedQuickSelect: setQuickSelect,
            }).map((e) => ({ ...e, isDefault: true }))}
            value={{
              id: selectedQuickSelect.quickSelect,
              name: "",
              isDefault: true,
              onClick: () => null,
            }}
            onChange={(filter) => {
              if (selectedQuickSelect.quickSelect === filter.id) {
                setQuickSelect("");
                setSelectedProducts([]);
                setSelectedPlatforms([]);
                setSelectedCampaigns([]);
                setSelectedAds([]);
              } else {
                setSelectedProducts([]);
                setSelectedPlatforms([]);
                setSelectedCampaigns([]);
                setSelectedAds([]);
                prettyLogEventPage({
                  domain: "Product Analytics",
                  action: "onClick",
                  additionalActionInfo: `Quick select clicked ${filter.name}`,
                });
                filter.onClick();
              }
            }}
          />
        )}
        <div
          className="flex items-center justify-center rounded-md border border-v2-notice bg-v2-highlight-notice py-1 px-2 text-v2-action-positive
        font-bold text-[10px] uppercase h-fit shrink-0"
        >
          UPDATED DAILY
        </div>
        <ChartControlsModal
          isModalOpen={chartControlsModalOpen}
          setIsModalOpen={setChartControlsModalOpen}
        />
      </div>
    </>
  );
};
